import React, { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import { LinkButtonWithChevron } from "components/LinkButtonWithChevron";
import cx from "classnames";
import { useConfiguratorPopupContext } from "providers";
import { LeftSidebarSubNavHeader } from "components/LeftSidebar/LeftSidebarSubNavHeader";
import { useGetSubnavItemTitle } from "hooks";
import type { LeftSidebarAcitveButton } from "interfaces/shared.types";
import { isProduction } from "utils/environment";

import s from "components/LeftSidebar/LeftSidebar.module.scss";

const singleObjClass = [
  "ProjectConfigurator",
  "ProjectItem",
  "ProjectItemSame",
];
const scrollToTop = () => {
  const divToScroll = document.querySelector<HTMLElement>("#application");
  if (divToScroll) {
    divToScroll.scrollTo({
      top: divToScroll.offsetTop,
      behavior: "smooth",
    });
  }
};

type FirstSubnavItemProps = {
  referenceObj: Scrivito.Obj;
  projectId?: string;
  seriesId?: string;
  setActiveButton: React.Dispatch<
    React.SetStateAction<null | LeftSidebarAcitveButton>
  >;
};

export const FirstSubnavItem = ({
  referenceObj,
  projectId,
  seriesId,
  setActiveButton,
}: FirstSubnavItemProps) => {
  const objClass = referenceObj.objClass();
  const linkTitle = useGetSubnavItemTitle(objClass);
  const isProdReady = referenceObj.get("isProdReady");
  const { isUpdateButtonActive, showConfiguratorPopup } =
    useConfiguratorPopupContext()!;

  const isDisabled = useMemo(
    () =>
      (!projectId && singleObjClass.some((item) => item === objClass)) ||
      (objClass === "ProductConfigurator" && !seriesId),
    [projectId, objClass, seriesId],
  );

  const urlParams = useMemo(():
    | { projectId?: string; seriesId?: string }
    | undefined => {
    if (objClass === "ProjectList") {
      return projectId ? { projectId } : undefined;
    }
    if (objClass === "ProjectListSame") {
      return projectId ? { projectId } : undefined;
    }
    if (objClass === "ProductConfigurator") {
      return { projectId, seriesId };
    }
    return { projectId };
  }, [objClass, seriesId, projectId]);

  const handleClick = useCallback(() => {
    const navigate = () => {
      scrollToTop();
      setActiveButton(null);

      if (referenceObj !== null) {
        Scrivito.navigateTo(referenceObj, urlParams);
      }
    };
    if (isUpdateButtonActive) {
      showConfiguratorPopup(navigate);
    } else {
      navigate();
    }
  }, [
    isUpdateButtonActive,
    setActiveButton,
    referenceObj,
    urlParams,
    showConfiguratorPopup,
  ]);

  if (isProdReady === false && isProduction) {
    return null;
  }

  return (
    <LinkButtonWithChevron
      data-testid={`LinkButtonWithChevron__${objClass}`}
      disabled={isDisabled}
      params={urlParams}
      className={cx(s.leftSidebarSubNavLink, {
        [s.leftSidebarSubNavLinkDisabled]: isDisabled,
      })}
      onClick={handleClick}
    >
      <LeftSidebarSubNavHeader referenceObj={referenceObj} title={linkTitle} />
    </LinkButtonWithChevron>
  );
};
